import React, { useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material/styles';
import GoboLogo from '@gobo/gobo.react/lib/core/GoboLogo';
import MaintenanceMessage from './MaintenanceMessage';

/**
 * Attempt to match CSS cubic-bezier(0.34, 1.56, 0.64, 1) with overshoot
 * Uses a simplified easing that overshoots then settles.
 *
 * @param {number} t - Progress from 0 to 1
 * @returns {number} - Eased value (can overshoot past 1)
 */
const springEasing = (t) => {
    // Back-out easing with overshoot (similar to cubic-bezier with y > 1)
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

/**
 * @typedef {object} LoginCardProps
 * @property {React.ReactNode} children - Content to render inside the card
 * @property {string} [title='Sign In'] - Title displayed at the top
 * @property {boolean} [showLogo=true] - Whether to show the logo
 * @property {'error'|'warning'|'info'|'success'} [severity] - Severity state for liquid glass tint (uses MUI palette)
 * @property {{ message: string, startTime: string, endTime: string, state: string }} [maintenanceMessage] - Optional maintenance message to display
 */

/**
 * Main container card for the login flow.
 * Features a modern animated height with snappy spring-like bounce
 * using ResizeObserver for smooth content transitions.
 * The glass border pseudo-elements rotate slightly based on height changes.
 *
 * @param {LoginCardProps} props - Component props
 * @returns {React.ReactElement}
 */
const LoginCard = ({ children, title = 'Sign In', showLogo = true, severity, maintenanceMessage }) => {
    const theme = useTheme();
    const contentRef = useRef(null);
    const wrapperRef = useRef(null);
    const [height, setHeight] = useState(undefined);
    
    // Get severity-based colors from theme palette
    const getSeverityColors = useCallback(() => {
        if (!severity) return null;
        
        const paletteColor = theme.palette[severity];
        if (!paletteColor) return null;
        
        return {
            main: paletteColor.main,
            light: paletteColor.light,
            // Background tint - subtle color mixed with dark base
            background: severity === 'error' 
                ? 'rgba(60, 30, 35, 0.35)'
                : severity === 'warning'
                ? 'rgba(60, 50, 30, 0.35)'
                : severity === 'info'
                ? 'rgba(30, 45, 60, 0.35)'
                : 'rgba(30, 55, 40, 0.35)', // success
        };
    }, [severity, theme.palette]);
    
    const severityColors = getSeverityColors();

    // Animated rotation angle that follows height with spring easing
    const [animatedAngle, setAnimatedAngle] = useState(0);
    const animationRef = useRef(null);
    const currentAngleRef = useRef(0); // Track current angle without causing re-renders
    const targetAngleRef = useRef(0);
    const startTimeRef = useRef(0);
    const startAngleRef = useRef(0);

    // Calculate target rotation angle based on height
    const calculateTargetAngle = useCallback((h) => {
        if (h === undefined) return 0;
        const baselineHeight = 400;
        const rotationPerPixel = 1 / 15; // 1 degree per 15px
        return -((h - baselineHeight) * rotationPerPixel);
    }, []);

    // Animate the rotation angle with spring easing when height changes
    useEffect(() => {
        if (height === undefined) return;

        const newTargetAngle = calculateTargetAngle(height);

        // If target hasn't changed significantly, skip animation
        if (Math.abs(newTargetAngle - targetAngleRef.current) < 0.01) return;

        // Cancel any ongoing animation
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }

        // Set up new animation - start from current position
        startAngleRef.current = currentAngleRef.current;
        targetAngleRef.current = newTargetAngle;
        startTimeRef.current = performance.now();

        const duration = 250; // Match the MUI short duration

        const animate = (currentTime) => {
            const elapsed = currentTime - startTimeRef.current;
            const progress = Math.min(elapsed / duration, 1);

            // Apply spring easing
            const easedProgress = springEasing(progress);

            const newAngle = startAngleRef.current +
                (targetAngleRef.current - startAngleRef.current) * easedProgress;

            currentAngleRef.current = newAngle;
            setAnimatedAngle(newAngle);

            if (progress < 1) {
                animationRef.current = requestAnimationFrame(animate);
            }
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height, calculateTargetAngle]); // Intentionally exclude animatedAngle to prevent animation restart

    // Calculate gradient positions from the animated angle
    const gradientPositions = (() => {
        const angleRad = (animatedAngle * Math.PI) / 180;

        // Center of rotation (50%, 50%)
        const cx = 50;
        const cy = 50;

        const rotatePoint = (x, y) => {
            const dx = x - cx;
            const dy = y - cy;
            const newX = cx + dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
            const newY = cy + dx * Math.sin(angleRad) + dy * Math.cos(angleRad);
            return { x: newX, y: newY };
        };

        const topShine = rotatePoint(30, 0);
        const bottomShine = rotatePoint(70, 100);

        return { topShine, bottomShine };
    })();

    // Use ResizeObserver to track content height changes
    useLayoutEffect(() => {
        const contentElement = contentRef.current;
        if (!contentElement) return;

        const updateHeight = () => {
            // Use scrollHeight to get the full content height including padding
            const newHeight = contentElement.scrollHeight;
            setHeight(newHeight);
        };

        // Initial measurement
        updateHeight();

        const resizeObserver = new ResizeObserver(() => {
            updateHeight();
        });

        resizeObserver.observe(contentElement);

        // Also observe children mutations to catch content changes
        const mutationObserver = new MutationObserver(() => {
            // Small delay to ensure DOM has updated
            requestAnimationFrame(updateHeight);
        });

        mutationObserver.observe(contentElement, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    return (
        <Box
            id="login-container"
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                minHeight: '100vh',
                p: 2,
            }}
        >
            <Fade in timeout={500}>
                <Card
                    sx={{
                        width: '100%',
                        maxWidth: 420,
                        // Liquid glass warped borders - organic, flowing shape
                        borderRadius: theme => `${theme.spacing(6)} ${theme.spacing(5)} ${theme.spacing(5.5)} ${theme.spacing(6.5)}`,
                        // Enhanced backdrop filter for stronger glass effect
                        backdropFilter: 'blur(40px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                        // Subtle color tint based on severity - keeps the glass feel with just a hint of color
                        backgroundColor: severityColors
                            ? severityColors.background
                            : theme => `rgba(${theme.vars.palette.background.paperChannel} / 0.25)`,
                        // Layered shadows for depth
                        boxShadow: theme => `
                            0 8px 32px rgba(0, 0, 0, 0.37),
                            inset 0 1px 0 rgba(255, 255, 255, 0.1),
                            inset 0 -1px 0 rgba(0, 0, 0, 0.1)
                        `,
                        border: 'unset',
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'visible',
                        // Smooth transitions for color changes - slightly slower so users notice
                        transition: theme.transitions.create(['background-color'], {
                            duration: theme.transitions.duration.standard * 1.5,
                            easing: theme.transitions.easing.easeInOut,
                        }),
                        
                        // Outer glow with warped border radius
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            overflow: 'visible',
                            inset: '-3px',
                            // Warped border radius matching card but slightly larger
                            borderRadius: theme => `calc(${theme.spacing(6)} + 3px) calc(${theme.spacing(5)} + 3px) calc(${theme.spacing(5.5)} + 3px) calc(${theme.spacing(6.5)} + 3px)`,
                            background: severityColors
                                ? `radial-gradient(
                                    ellipse at ${gradientPositions.topShine.x}% ${gradientPositions.topShine.y}%, 
                                    ${severityColors.main}99 0%, 
                                    transparent 60%
                                ), radial-gradient(
                                    ellipse at ${gradientPositions.bottomShine.x}% ${gradientPositions.bottomShine.y}%, 
                                    ${severityColors.main}66 0%, 
                                    transparent 60%
                                )`
                                : `radial-gradient(
                                    ellipse at ${gradientPositions.topShine.x}% ${gradientPositions.topShine.y}%, 
                                    rgba(255, 255, 255, 0.35) 0%, 
                                    transparent 60%
                                ), radial-gradient(
                                    ellipse at ${gradientPositions.bottomShine.x}% ${gradientPositions.bottomShine.y}%, 
                                    rgba(255, 255, 255, 0.2) 0%, 
                                    transparent 60%
                                )`,
                            filter: 'blur(12px)',
                            opacity: 0.6,
                            zIndex: -1,
                            pointerEvents: 'none',
                            transition: theme.transitions.create(['background'], {
                                duration: theme.transitions.duration.standard * 1.5,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                        },
                        
                        // Liquid glass border - gradient with warped edges
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            // Warped border radius
                            borderRadius: theme => `${theme.spacing(6)} ${theme.spacing(5)} ${theme.spacing(5.5)} ${theme.spacing(6.5)}`,
                            padding: '1.5px',
                            background: severityColors
                                ? `
                                    radial-gradient(
                                        ellipse at ${gradientPositions.topShine.x}% ${gradientPositions.topShine.y}%, 
                                        ${severityColors.light} 0%, 
                                        ${severityColors.main}40 30%,
                                        ${severityColors.main}1A 50%
                                    ),
                                    radial-gradient(
                                        ellipse at ${gradientPositions.bottomShine.x}% ${gradientPositions.bottomShine.y}%, 
                                        ${severityColors.main}B3 0%, 
                                        ${severityColors.main}33 35%,
                                        ${severityColors.main}0D 55%
                                    ),
                                    linear-gradient(
                                        135deg,
                                        ${severityColors.main}33 0%,
                                        ${severityColors.main}1A 50%,
                                        ${severityColors.main}26 100%
                                    )
                                `
                                : `
                                    radial-gradient(
                                        ellipse at ${gradientPositions.topShine.x}% ${gradientPositions.topShine.y}%, 
                                        rgba(255, 255, 255, 0.9) 0%, 
                                        rgba(255, 255, 255, 0.15) 30%,
                                        rgba(255, 255, 255, 0.05) 50%
                                    ),
                                    radial-gradient(
                                        ellipse at ${gradientPositions.bottomShine.x}% ${gradientPositions.bottomShine.y}%, 
                                        rgba(255, 255, 255, 0.7) 0%, 
                                        rgba(255, 255, 255, 0.1) 35%,
                                        rgba(255, 255, 255, 0.03) 55%
                                    ),
                                    linear-gradient(
                                        135deg,
                                        rgba(255, 255, 255, 0.1) 0%,
                                        rgba(255, 255, 255, 0.05) 50%,
                                        rgba(255, 255, 255, 0.08) 100%
                                    )
                                `,
                            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                            WebkitMaskComposite: 'xor',
                            maskComposite: 'exclude',
                            pointerEvents: 'none',
                            transition: theme.transitions.create(['background'], {
                                duration: theme.transitions.duration.standard * 1.5,
                                easing: theme.transitions.easing.easeInOut,
                            }),
                        },
                    }}
                >
                    {/* Animated height wrapper */}
                    <Box
                        ref={wrapperRef}
                        sx={{
                            // Explicit height enables CSS transitions (auto doesn't animate)
                            height: height !== undefined ? height : 'auto',
                            overflow: 'hidden',
                            // Animated height with snappy spring bounce
                            // cubic-bezier(0.34, 1.56, 0.64, 1) creates a slight overshoot for modern "pop" feel
                            transition: theme =>
                                theme.transitions.create(['height'], {
                                    duration: theme.transitions.duration.short,
                                    easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                                }),
                        }}
                    >
                        {/* Content to measure - positioned absolutely for accurate measurement */}
                        <Box ref={contentRef}>
                            <CardContent
                                sx={{
                                    p: theme => theme.spacing(4),
                                    pb: theme => theme.spacing(6),
                                    '&:last-child': {
                                        pb: theme => theme.spacing(6),
                                    },
                                }}
                            >
                                {showLogo && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <GoboLogo style={{
                                            width: '75%'
                                        }} />
                                    </Box>
                                )}

                                {maintenanceMessage && (
                                    <MaintenanceMessage
                                        message={maintenanceMessage.message}
                                        startTime={maintenanceMessage.startTime}
                                        endTime={maintenanceMessage.endTime}
                                        state={maintenanceMessage.state}
                                    />
                                )}

                                <Box>{children}</Box>
                            </CardContent>
                        </Box>
                    </Box>
                </Card>
            </Fade>
        </Box >
    );
};

LoginCard.propTypes = {
    children: PropTypes.node.isRequired,
    title: PropTypes.string,
    showLogo: PropTypes.bool,
    severity: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
    maintenanceMessage: PropTypes.shape({
        message: PropTypes.string.isRequired,
        startTime: PropTypes.string,
        endTime: PropTypes.string,
        state: PropTypes.string,
    }),
};

export default LoginCard;
